import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { LotCard } from '../components/card/lot-card';
import { FormatAmount } from '../components/format-amount/format-amount';
import { Image } from '../components/image/image';
import { Label, LabelVariant } from '../components/label/label';
import { Auction, Bid, Lot } from '../graphql/types';
import { color } from '../styles/mixins';
import { useLotTimer } from '../utils/use-lot-timer';

type LotItem = Pick<
  Lot,
  | 'id'
  | 'title'
  | 'number'
  | 'startsAt'
  | 'endsAt'
  | 'vintner'
  | 'vintage'
  | 'images'
  | 'summary'
  | 'variety'
> & {
  highestBids: Bid[];
  yourBid?: Partial<Bid>;
};

const Timer = styled.div`
  color: ${color('red60')};
`;

const Ended = styled.div`
  color: ${color('red60')};
`;

export function timerText(ms: number) {
  const diff = Math.floor(ms / 1000);
  if (diff <= 0) {
    return null;
  }

  const seconds = diff % 60;
  const minutes = Math.floor(diff / 60) % 60;
  const hours = Math.floor(diff / 60 / 60) % 24;
  const days = Math.floor(diff / 60 / 60 / 24);

  const output = [days, hours, minutes, seconds]
    .map((v, i) =>
      i === 0 && v === 0 ? null : (v || 0).toString().padStart(2, '0')
    )
    .filter((n) => !!n)
    .join(':');

  return output;
}

export function LotCardContainer({
  lot,
  auction,
  yourbid,
}: {
  lot: LotItem;
  auction?: Pick<
    Auction,
    | 'startsAt'
    | 'endsAt'
    | 'buyersPremiumPercentage'
    | 'overheadPremiumPercentage'
  >;
  yourbid?: Partial<Bid>;
}) {
  const { isStarted, isEnded, startsIn } = useLotTimer({
    startsAt: lot.startsAt ?? auction?.startsAt,
    endsAt: lot.endsAt ?? auction?.endsAt,
  });
  const highestBids = lot.highestBids ?? [];
  const yourBidIndex = highestBids.findIndex(
    (bid) => bid.id === lot.yourBid?.id
  );
  const [highestBid] = highestBids;

  return (
    <Link to={`/lot/${lot.id}`}>
      <LotCard
        status={
          lot.yourBid ? (
            yourBidIndex >= 0 ? (
              <Label variant={LabelVariant.Success}>
                You’re {yourBidIndex === 0 ? `a Leader` : 'on the Leaderboard'}
              </Label>
            ) : (
              <Label variant={LabelVariant.Warning}>Outbid</Label>
            )
          ) : undefined
        }
        image={
          <Image
            src={
              lot.images?.find((i: any) => i.size === 'thumb')?.url ??
              '/temp/image_example_tall_2.png'
            }
            loading="eager"
            fit="contain"
            alt={lot.title}
          />
        }
        brandName={`${lot.number}. ${lot.vintner}`}
        title={`${lot.vintage} ${lot.title}`}
        highestBid={highestBid?.amount}
        yourBid={yourbid?.amount}
        timer={
          !isStarted ? (
            <Timer>Starts in {timerText(startsIn)}</Timer>
          ) : isEnded ? (
            <Ended>
              Lot ended
              {highestBid?.amount ? (
                <>
                  {' '}
                  for{' '}
                  <FormatAmount
                    value={highestBid.amount}
                    buyersPremiumPercentage={auction?.buyersPremiumPercentage}
                    overheadPremiumPercentage={
                      auction?.overheadPremiumPercentage
                    }
                  />
                </>
              ) : null}
            </Ended>
          ) : null
        }
        summary={
          lot.summary === '' ? lot.title : `${lot.summary} - ${lot.variety}`
        }
        isLotClosed={isEnded}
        buyersPremiumPercentage={auction?.buyersPremiumPercentage}
        overheadPremiumPercentage={auction?.overheadPremiumPercentage}
      />
    </Link>
  );
}
